import React, { useState, useEffect } from "react"
import SimpleBar from "simplebar-react"
import withRouter from "components/Common/withRouter"
import { Link, useLocation } from "react-router-dom"
import { withTranslation } from "react-i18next"
import {
  FaCalendarAlt,
  FaHistory,
  FaHome,
  FaHotdog,
  FaSalesforce,
  FaUsers,
  FaChartPie,
  FaChevronDown,
  FaUserTie,
  FaFileExcel,
} from "react-icons/fa"
import { MdStackedLineChart } from "react-icons/md"
import Configs from "../../config"
import PropTypes from "prop-types"
import { FaArrowsLeftRight, FaScaleBalanced } from "react-icons/fa6"
import { BsFilePdf, BsGraphUpArrow } from "react-icons/bs"

const SidebarContent = () => {
  const ref = React.useRef()
  const [openSubmenus, setOpenSubmenus] = useState({})
  const { pathname } = useLocation()

  const isActive = path => pathname === `${Configs.appurl}${path}`

  const menuItems = [
    {
      to: "dashboard",
      icon: <FaHome size={18} className="me-2" />,
      label: "Home",
    },
    {
      to: "directors-report",
      icon: <FaUserTie size={16} className="me-2 mb-1" />,
      label: "Director's Report",
    },
    {
      to: "dashboard-history",
      icon: <FaHistory size={14} className="me-2 mb-1" />,
      label: "History Chart",
    },
    {
      to: "sales-Revenue",
      icon: <FaSalesforce size={18} className="me-2 mb-1" />,
      label: "Sales Revenue",
    },
    {
      to: "suasage",
      icon: <FaHotdog size={18} className="me-2 mb-1" />,
      label: "Sausage Category",
    },
    {
      to: "gmp",
      icon: <FaCalendarAlt size={18} className="me-2 mb-1" />,
      label: "GMP",
    },
    {
      to: "oee",
      icon: <FaUsers size={18} className="me-2 mb-1" />,
      label: "OEE",
    },
    {
      to: "plcharts",
      icon: <MdStackedLineChart size={18} className="me-2 mb-1" />,
      label: "Profit & Loss",
    },
    {
      to: "net-gross-margin",
      icon: <FaScaleBalanced size={16} className="me-2 mb-1" />,
      label: "Net & Gross Margin",
    },
    {
      to: "net-margin",
      icon: <FaChartPie size={16} className="me-2 mb-1" />,
      label: "Customer Report",
    },
    {
      to: "sales-report",
      icon: <BsGraphUpArrow size={18} className="me-2" />,
      label: "Sales Report",
    },
    {
      to: "guage-range",
      icon: <FaArrowsLeftRight size={18} className="me-2" />,
      label: "Gauge Ranges",
    },

    {
      to: "logs",
      icon: <BsFilePdf size={18} className="me-2" />,
      label: "Sheet Logs",
    },
    {
      to: "master-sheet-reference",
      icon: (
        <FaFileExcel
          size={16}
          className="me-2 mb-1"
          style={{ flexShrink: 0, minWidth: 16 }}
        />
      ),
      label: "Master Sheet Reference",
    },
  ]

  // Auto-expand a parent whose child route is currently active.
  useEffect(() => {
    const next = {}
    menuItems.forEach((item, index) => {
      if (item.children?.some(child => isActive(child.to))) {
        next[index] = true
      }
    })
    setOpenSubmenus(prev => ({ ...prev, ...next }))
  }, [pathname])

  const toggleSubmenu = index => {
    setOpenSubmenus(prev => ({ ...prev, [index]: !prev[index] }))
  }

  return (
    <React.Fragment>
      <SimpleBar ref={ref} style={{ height: "calc(100vh - 70px)" }}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            {menuItems.map((item, index) => {
              const hasChildren = item.children?.length > 0
              const isOpen = !!openSubmenus[index]
              const active = isActive(item.to)

              return (
                <li key={index} className="mx-2">
                  {hasChildren ? (
                    <>
                      {/* Fully inline-styled — no theme active/mm-active classes,
                          so nothing else can override the colors. */}
                      <div style={{ display: "flex", alignItems: "stretch" }}>
                        <Link
                          to={`${Configs.appurl}${item.to}`}
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            padding: "0.625rem 0 0.625rem 1.5rem",
                            fontSize: "13px",
                            background: active ? "#057aae" : "transparent",
                            color: active ? "#fff" : "#545a6d",
                            borderRadius: active ? "6px 0 0 6px" : 0,
                          }}
                        >
                          {item.icon}
                          {item.label}
                        </Link>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-haspopup="true"
                          aria-expanded={isOpen}
                          aria-label="Toggle submenu"
                          onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                            toggleSubmenu(index)
                          }}
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault()
                              e.stopPropagation()
                              toggleSubmenu(index)
                            }
                          }}
                          style={{
                            padding: "0 14px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            background: active ? "#057aae" : "transparent",
                            borderRadius: active ? "0 6px 6px 0" : 0,
                            color: active ? "#fff" : "#6c757d",
                          }}
                        >
                          <FaChevronDown
                            size={11}
                            style={{
                              transition: "transform 0.15s ease",
                              transform: isOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              opacity: 0.7,
                              pointerEvents: "none",
                            }}
                          />
                        </span>
                      </div>

                      {/* In-flow accordion, VS Code tree style — pushes items
                          below it down, no fixed/hover popup. */}
                      {isOpen && (
                        <ul
                          className="list-unstyled"
                          style={{
                            margin: "2px 0 4px",
                            paddingLeft: "20px",
                            borderLeft: "2px solid #e9ecef",
                            marginLeft: "20px",
                          }}
                        >
                          {item.children.map((child, ci) => {
                            const childIsActive = isActive(child.to)
                            return (
                              <li key={ci} style={{ margin: "2px 6px" }}>
                                <Link
                                  to={`${Configs.appurl}${child.to}`}
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: childIsActive ? 600 : 400,
                                    padding: "8px 12px",
                                    display: "flex",
                                    alignItems: "center",
                                    whiteSpace: "nowrap",
                                    borderRadius: "6px",
                                    color: childIsActive ? "#fff" : "#545a6d",
                                    background: childIsActive
                                      ? "#057aae"
                                      : "transparent",
                                  }}
                                >
                                  {child.icon}
                                  {child.label}
                                </Link>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={`${Configs.appurl}${item.to}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.625rem 1.5rem",
                        fontSize: "13px",
                        background: active ? "#057aae" : "transparent",
                        color: active ? "#fff" : "#545a6d",
                        borderRadius: "6px",
                      }}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))
